import { FC } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, Sparkles, Link as LinkIcon } from "lucide-react";
import { TemplateMessageSection } from "./form/TemplateMessageSection";
import { useAddLinkForm } from "@/hooks/useAddLinkForm";

interface AddLinkFormProps {
  onSubmit: (linkData: any) => Promise<boolean>;
}

export const AddLinkForm: FC<AddLinkFormProps> = ({ onSubmit }) => {
  const {
    title,
    setTitle,
    description,
    setDescription,
    whatsappNumber,
    setWhatsappNumber,
    messageTemplate,
    setMessageTemplate,
    isLoading,
    isSuccess,
    handleSubmit,
  } = useAddLinkForm(onSubmit);

  const formatText = (format: "bold" | "italic") => {
    const formats = {
      bold: "*",
      italic: "_",
    };
    const marker = formats[format];
    setMessageTemplate((prev) => `${prev}${marker}text${marker}`);
  };

  const insertEmoji = (emoji: string) => {
    setMessageTemplate((prev) => prev + emoji);
  };

  return (
    <section className="bg-gradient-to-b from-green-50/50 to-white/50 dark:from-green-900/20 dark:to-black/20 backdrop-blur-lg py-12 md:py-20">
      <div className="container mx-auto px-4 max-w-2xl">
        <Card className={`p-6 md:p-8 shadow-xl border-2 border-green-100 dark:border-green-800 hover:border-green-200 dark:hover:border-green-700 transition-all ${isSuccess ? 'animate-wiggle' : ''}`}>
          <div className="flex items-center justify-center mb-6 space-x-2">
            <Sparkles className={`w-8 h-8 text-green-500 ${isSuccess ? 'animate-spin' : ''}`} />
            <h2 className="text-2xl md:text-3xl font-black bg-gradient-to-r from-green-600 to-green-400 bg-clip-text text-transparent">
              Buat Link Produk
            </h2>
            <LinkIcon className={`w-8 h-8 text-green-500 ${isSuccess ? 'animate-bounce' : ''}`} />
          </div>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="whatsapp" className="text-lg font-bold text-green-700 dark:text-green-300">
                Nomor WhatsApp
              </Label>
              <Input
                id="whatsapp"
                placeholder="628123456789"
                value={whatsappNumber}
                onChange={(e) => setWhatsappNumber(e.target.value)}
                className="text-lg border-2 border-green-100 dark:border-green-800 focus:border-green-500"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="title" className="text-lg font-bold text-green-700 dark:text-green-300">
                Nama Produk (minimal 5 karakter)
              </Label>
              <Input
                id="title"
                placeholder="nama-produk-kamu"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="text-lg border-2 border-green-100 dark:border-green-800 focus:border-green-500"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="description" className="text-lg font-bold text-green-700 dark:text-green-300">
                Deskripsi Produk
              </Label>
              <Textarea
                id="description"
                placeholder="Deskripsi lengkap produk Anda"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="text-lg border-2 border-green-100 dark:border-green-800 focus:border-green-500 min-h-[100px]"
              />
            </div>
            <TemplateMessageSection
              messageTemplate={messageTemplate}
              onMessageTemplateChange={setMessageTemplate}
              onFormatText={formatText}
              onInsertEmoji={insertEmoji}
            />
            <Button
              type="submit"
              disabled={isLoading}
              className={`w-full h-14 text-lg font-bold bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 transform hover:scale-105 transition-all duration-200 shadow-lg ${isSuccess ? 'animate-pulse' : ''}`}
            >
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-6 w-6 animate-spin" />
                  Memproses
                </>
              ) : (
                <>
                  <Sparkles className="mr-2 h-6 w-6" />
                  Buat Link
                </>
              )}
            </Button>
          </form>
        </Card>
      </div>
    </section>
  );
};